import { Box, Skeleton } from "@mui/material";
import {
  ColumnDef,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import React, { useDeferredValue, useMemo } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { ESTIMATED_ROW_HEIGHT } from "./constants";
import { DesktopTableView } from "./DesktopTableView";
import { MobileTableView } from "./MobileTableView";
import { TableToolbar } from "./TableToolbar";
import { DataTableProps } from "./types";
import { useTableState } from "./useTableState";

const TABLE_INITIAL_STATE = {};
const EMPTY: unknown[] = [];

export const DataTable = <T extends object>(
  props: DataTableProps<T>,
): React.JSX.Element => {
  const {
    data,
    columns,
    facetedColumnIds,
    globalFilterPlaceholder,
    filterSlot,
    onRowClick,
    onActionClick,
    actionButtonText,
    actionButtonIcon,
    showActionButton = true,
    estimatedRowHeight = ESTIMATED_ROW_HEIGHT,
    defaultSort,
    onSortingChange,
    actionButtonTextShort,
    isLoading = false,
    virtualize = true,
    overscan = 8,
    getRowId,
    height,
    onFilteredDataChange,
    tableKey,
  } = props;

  const facetedIds = useMemo(
    () => Array.from(facetedColumnIds ?? []),
    [facetedColumnIds],
  );

  const sortableColumnIds = useMemo(() => {
    const ids = new Set<string>();
    for (const column of columns) {
      const id =
        column.id ??
        ("accessorKey" in column ? String(column.accessorKey) : undefined);
      if (id) ids.add(id);
    }

    return ids;
  }, [columns]);

  const {
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters,
    globalFilterValue,
    setGlobalFilterValue,
  } = useTableState(defaultSort, tableKey, sortableColumnIds);

  const deferredGlobalFilter = useDeferredValue(globalFilterValue.trim());

  React.useEffect(() => {
    onSortingChange?.(sorting);
  }, [sorting, onSortingChange]);

  const handleOnSortingChange = React.useCallback<OnChangeFn<SortingState>>(
    (updaterOrValue) => {
      setSorting((prev) => {
        const next =
          typeof updaterOrValue === "function"
            ? updaterOrValue(prev)
            : updaterOrValue;

        if (next.length === 0 && prev.length > 0) {
          const current = prev[0];
          if (current?.id) return [{ id: current.id, desc: !current.desc }];
        }

        return next;
      });
    },
    [setSorting],
  );

  const tableData = useMemo(() => (data ?? EMPTY) as T[], [data]);
  const tableColumns = useMemo(() => columns as ColumnDef<T, any>[], [columns]);

  const table = useReactTable<T>({
    data: tableData,
    columns: tableColumns,
    state: {
      sorting,
      columnFilters,
      globalFilter: deferredGlobalFilter,
    },
    onSortingChange: handleOnSortingChange,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(facetedIds.length > 0
      ? {
          getFacetedRowModel: getFacetedRowModel(),
          getFacetedUniqueValues: getFacetedUniqueValues(),
        }
      : null),
    initialState: TABLE_INITIAL_STATE,
    ...(getRowId ? { getRowId } : {}),
  });

  const rows = table.getRowModel().rows;

  const rowsLen = rows.length;
  const rowsKey = `${rowsLen}:${rows[0]?.id ?? ""}:${rows[Math.floor(rowsLen / 2)]?.id ?? ""}:${rows[rowsLen - 1]?.id ?? ""}`;

  React.useEffect(() => {
    onFilteredDataChange?.(rows.map((r) => r.original));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsKey, onFilteredDataChange]);

  const isMobile = useIsMobile();
  const showSkeleton = isLoading && (data?.length ?? 0) === 0;

  const isStale = globalFilterValue.trim() !== deferredGlobalFilter;

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <TableToolbar
        actionButtonIcon={actionButtonIcon}
        actionButtonText={actionButtonText}
        actionButtonTextShort={actionButtonTextShort}
        columns={table.getAllColumns()}
        facetedColumnIds={facetedIds}
        filterSlot={filterSlot}
        globalFilterPlaceholder={globalFilterPlaceholder}
        globalFilterValue={globalFilterValue}
        onActionClick={onActionClick}
        onSortingChange={setSorting}
        setGlobalFilter={setGlobalFilterValue}
        setGlobalFilterValue={setGlobalFilterValue}
        showActionButton={showActionButton}
        sorting={sorting}
      />

      {showSkeleton ? (
        <Box sx={{ p: 0 }}>
          {!isMobile && (
            <Skeleton
              height={50}
              sx={{ mb: 1, borderRadius: 1 }}
              variant="rectangular"
              width="100%"
            />
          )}
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton
              height={isMobile ? 100 : 60}
              key={i}
              sx={{ mb: 1, borderRadius: 1 }}
              variant="rectangular"
              width="100%"
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ opacity: isStale ? 0.5 : 1, transition: "opacity 0.2s" }}>
          {isMobile ? (
            <MobileTableView<T>
              estimatedRowHeight={estimatedRowHeight}
              height={height}
              onRowClick={onRowClick}
              overscan={overscan}
              table={table}
              virtualize={virtualize}
            />
          ) : (
            <DesktopTableView<T>
              estimatedRowHeight={estimatedRowHeight}
              facetedColumnIds={facetedIds}
              height={height}
              onRowClick={onRowClick}
              overscan={overscan}
              table={table}
              virtualize={virtualize}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
